import { Check, ChevronsRight, X } from "lucide-react";
import { useState } from "react";
import { EntityPicker } from "@/components/EntityPicker";
import { useApp } from "@/lib/AppContext";
import { ApiError, type Entity, type EntityType, type OwnerQuestion } from "@/lib/api";
import { cn } from "@/lib/cn";
import { entityTypeLabel } from "@/lib/entityTypes";

/**
 * UI-21 — one question the system is asking, and the one way to answer it.
 *
 * **Each kind has exactly one answer shape, and the card offers only that**: a role field, a
 * team picker, a person picker for who leads, a person picker for who owes, or a create-entity
 * form already filled in with the name. And **nothing here is a second write path** — every
 * answer is the write a page already makes: the dossier's role, UI-20's relation, AUTO-4's loop,
 * WORLD-9's accepted suggestion, SPCH-6's confirmed voice. The Server's sweep notices the gap is
 * filled and the question closes; nobody marks a question answered.
 *
 * Shared by the three surfaces that render the Needs-you stream — the desktop Review queue, simple
 * mode and the phone — the way the belief renderer is: one component, the surface decides only
 * the scale. "Don't ask again" is the one answer that is about the question itself.
 */
export function QuestionAnswer({
  question,
  onAnswered,
  onSkip,
  scale = "desktop",
}: {
  question: OwnerQuestion;
  /**
   * Called once the write landed. `dismissed` says it was "Don't ask again" rather than an answer;
   * an answer can close more than this card (a role places a person, closing their team card too),
   * which is why a surface reloads its queue after one.
   */
  onAnswered: (outcome: { dismissed: boolean }) => void;
  onSkip?: () => void;
  scale?: "desktop" | "simple" | "phone";
}) {
  const { api } = useApp();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function run(work: () => Promise<unknown>, dismissed = false) {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      await work();
      onAnswered({ dismissed });
    } catch (cause: unknown) {
      setProblem(
        cause instanceof ApiError
          ? cause.status === 404
            ? "That has already been answered somewhere else."
            : cause.message
          : "That could not be saved. Try again.",
      );
      setBusy(false);
    }
  }

  const big = scale !== "desktop";

  return (
    <div className="flex flex-col gap-[var(--space-3)]">
      <p
        className={cn(
          "font-bold tracking-[var(--tracking-snug)] text-[var(--text-primary)] [text-wrap:pretty]",
          scale === "simple" ? "text-[26px] leading-[1.25]" : scale === "phone" ? "text-[20px] leading-[1.3]" : "text-[length:var(--text-lg)]",
        )}
      >
        {question.question}
      </p>

      {question.detail && (
        <p
          className={cn(
            "leading-[1.6] text-[var(--text-secondary)] [text-wrap:pretty]",
            question.kind === "commitment_counterparty" && "italic text-[var(--text-primary)]",
            big ? "text-[length:var(--text-md)]" : "text-[length:var(--text-sm)]",
          )}
        >
          {question.kind === "commitment_counterparty" ? `“${question.detail}”` : question.detail}
        </p>
      )}

      <AnswerShape question={question} busy={busy} big={big} onRun={(work) => void run(work)} />

      {problem && (
        <p role="alert" className="text-[length:var(--text-xs)] text-[var(--warning)]">
          {problem}
        </p>
      )}

      <div className="flex items-center gap-[var(--space-3)] border-t border-[var(--border-subtle)] pt-[var(--space-2)]">
        {onSkip && (
          <button
            type="button"
            onClick={onSkip}
            className="inline-flex items-center gap-1.5 text-[length:var(--text-xs)] text-[var(--text-muted)] transition hover:text-[var(--text-secondary)]"
          >
            <ChevronsRight size={12} strokeWidth={2} />
            Skip for now
          </button>
        )}
        <span className="flex-1" />
        <button
          type="button"
          disabled={busy}
          onClick={() => void run(() => api.dismissQuestion(question.id), true)}
          className="inline-flex items-center gap-1.5 text-[length:var(--text-xs)] text-[var(--text-muted)] transition hover:text-[var(--text-secondary)] disabled:opacity-50"
        >
          <X size={12} strokeWidth={2} />
          Don&rsquo;t ask again
        </button>
      </div>
    </div>
  );
}

/** The one answer each kind takes. */
function AnswerShape({
  question,
  busy,
  big,
  onRun,
}: {
  question: OwnerQuestion;
  busy: boolean;
  big: boolean;
  onRun: (work: () => Promise<unknown>) => void;
}) {
  const { api, entities } = useApp();

  switch (question.kind) {
    case "person_role":
      return (
        <TextAnswer
          label="Role"
          placeholder="What they do, in a few words — “BI Engineer”"
          initial=""
          submit="Save role"
          busy={busy}
          big={big}
          onSubmit={(role) => onRun(() => api.setPersonRole(question.targetId, role))}
        />
      );

    case "person_team":
      return (
        <PickAnswer
          label="Team"
          entities={entities}
          kinds={["team"]}
          submit="They are on this team"
          busy={busy}
          onSubmit={(team) =>
            onRun(() =>
              api.assertRelation(question.targetId, { relationType: "member_of", otherEntityId: team.id, thisIsSubject: true }),
            )
          }
        />
      );

    case "initiative_lead":
      return (
        <PickAnswer
          label="Who leads it"
          entities={entities}
          kinds={["person"]}
          submit="They lead it"
          busy={busy}
          onSubmit={(person) =>
            onRun(() =>
              api.assertRelation(question.targetId, { relationType: "leads", otherEntityId: person.id, thisIsSubject: false }),
            )
          }
        />
      );

    case "commitment_counterparty":
      return (
        <PickAnswer
          label="Who owes it"
          entities={entities}
          kinds={["person"]}
          submit="They owe it"
          busy={busy}
          onSubmit={(person) => onRun(() => api.setCommitmentCounterparty(question.targetId, person.id))}
        />
      );

    case "speaker_person":
      return (
        <TextAnswer
          label="Name"
          placeholder="Their name"
          initial={question.name}
          submit="Create this person"
          hint="Creates the person and names the voice after them."
          busy={busy}
          big={big}
          onSubmit={(name) =>
            question.recordingId
              ? onRun(() => api.confirmSpeaker(question.recordingId!, question.targetId, { name }))
              : undefined
          }
        />
      );

    case "subject_entity":
      return <CreateEntityAnswer question={question} busy={busy} big={big} onRun={onRun} />;
  }
}

function TextAnswer({
  label,
  placeholder,
  initial,
  submit,
  hint,
  busy,
  big,
  onSubmit,
}: {
  label: string;
  placeholder: string;
  initial: string;
  submit: string;
  hint?: string;
  busy: boolean;
  big: boolean;
  onSubmit: (value: string) => void;
}) {
  const [value, setValue] = useState(initial);
  const trimmed = value.trim();

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (trimmed) onSubmit(trimmed);
      }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder={placeholder}
          aria-label={label}
          className={cn(
            "min-w-0 flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-2)] px-3 text-[var(--text-primary)] outline-none focus:border-[var(--ring)]",
            big ? "h-11 text-[length:var(--text-md)]" : "h-9 text-[length:var(--text-sm)]",
          )}
        />
        <SubmitButton label={submit} busy={busy} disabled={!trimmed} big={big} />
      </div>
      {hint && <span className="text-[length:var(--text-2xs)] text-[var(--text-muted)]">{hint}</span>}
    </form>
  );
}

function PickAnswer({
  label,
  entities,
  kinds,
  submit,
  busy,
  onSubmit,
}: {
  label: string;
  entities: Entity[];
  kinds: EntityType[];
  submit: string;
  busy: boolean;
  onSubmit: (entity: Entity) => void;
}) {
  const [picked, setPicked] = useState<Entity | null>(null);
  const offered = entities.filter((entity) => kinds.includes(entity.type));

  if (offered.length === 0) {
    return (
      <p className="text-[length:var(--text-sm)] text-[var(--text-muted)]">
        There is no {kinds.map((kind) => entityTypeLabel(kind).toLowerCase()).join(" or ")} to pick yet. Add one in
        Entities, then come back to this.
      </p>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <EntityPicker entities={entities} kinds={kinds} value={picked} onChange={setPicked} label={label} />
      <button
        type="button"
        disabled={busy || !picked}
        onClick={() => picked && onSubmit(picked)}
        className="inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-md)] bg-[var(--primary)] px-3 text-[length:var(--text-sm)] font-semibold text-[var(--primary-foreground)] transition hover:brightness-110 disabled:opacity-50"
      >
        <Check size={14} strokeWidth={2.5} />
        {busy ? "Saving…" : submit}
      </button>
    </div>
  );
}

/** The kinds a page with nothing behind it can become — the ones the sweep asks about. */
const PAGE_KINDS: EntityType[] = ["person", "product", "service"];

function CreateEntityAnswer({
  question,
  busy,
  big,
  onRun,
}: {
  question: OwnerQuestion;
  busy: boolean;
  big: boolean;
  onRun: (work: () => Promise<unknown>) => void;
}) {
  const { api } = useApp();
  const [kind, setKind] = useState<EntityType>(
    PAGE_KINDS.includes(question.entityKind as EntityType) ? (question.entityKind as EntityType) : "person",
  );
  const [name, setName] = useState(question.name);
  const [role, setRole] = useState("");

  const field = cn(
    "rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-2)] px-3 text-[var(--text-primary)] outline-none focus:border-[var(--ring)]",
    big ? "h-11 text-[length:var(--text-md)]" : "h-9 text-[length:var(--text-sm)]",
  );

  if (!question.suggestionId) {
    return (
      <p className="text-[length:var(--text-sm)] text-[var(--text-muted)]">
        This page cannot be turned into an entity from here yet. Try again in a moment.
      </p>
    );
  }

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (!name.trim()) return;
        onRun(() =>
          api.acceptEntitySuggestion(question.suggestionId!, {
            type: kind,
            name: name.trim(),
            role: kind === "person" && role.trim() ? role.trim() : null,
          }),
        );
      }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={kind}
          onChange={(event) => setKind(event.target.value as EntityType)}
          aria-label="Kind"
          className={field}
        >
          {PAGE_KINDS.map((option) => (
            <option key={option} value={option}>
              {entityTypeLabel(option)}
            </option>
          ))}
        </select>
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          aria-label="Name"
          className={cn(field, "min-w-[12ch] flex-1")}
        />
      </div>
      {kind === "person" && (
        <input
          value={role}
          onChange={(event) => setRole(event.target.value)}
          aria-label="Role"
          placeholder="Their role, if you know it — optional"
          className={field}
        />
      )}
      <div className="flex items-center gap-2">
        <span className="text-[length:var(--text-2xs)] text-[var(--text-muted)]">
          Creates the {entityTypeLabel(kind).toLowerCase()} and puts this page behind it.
        </span>
        <span className="flex-1" />
        <SubmitButton label={`Create ${entityTypeLabel(kind).toLowerCase()}`} busy={busy} disabled={!name.trim()} big={big} />
      </div>
    </form>
  );
}

function SubmitButton({ label, busy, disabled, big }: { label: string; busy: boolean; disabled: boolean; big: boolean }) {
  return (
    <button
      type="submit"
      disabled={busy || disabled}
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-[var(--radius-md)] bg-[var(--primary)] px-[var(--space-4)] font-semibold text-[var(--primary-foreground)] transition hover:brightness-110 disabled:opacity-50",
        big ? "h-11 text-[length:var(--text-md)]" : "h-9 text-[length:var(--text-sm)]",
      )}
    >
      <Check size={14} strokeWidth={2.5} />
      {busy ? "Saving…" : label}
    </button>
  );
}
